import {
  Button,
  Callout,
  Checkbox,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Field,
  Input,
  NativeSelect,
  RadioCard,
  RadioGroup,
  Textarea,
  useToast,
} from '@provenance/design';
import { ALL_API_SCOPES, type CreatedApiKey } from '@provenance/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Check, Copy, Eye, KeyRound, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * API KEY LIFECYCLE — spec v3 §20.2/§20.3
 *
 * Three dialogs, each shaped by a property of the credential rather than by convenience:
 *
 *  CREATE shows the secret exactly once, behind an explicit acknowledgement that cannot
 *  be skipped. Only a hash is stored, so "show it to me again" is not a feature we can
 *  add later — which means the UI has to make that unmissable at the moment it matters.
 *
 *  ROTATE keeps the old key working for an overlap window. Immediate revocation on
 *  rotation would break the integrator's production traffic the instant the button is
 *  pressed, which is exactly why nobody rotates credentials in systems built that way.
 *  The dialog explains the overlap rather than hiding it behind a number field.
 *
 *  REVOKE requires the key's label typed back. It is immediate, irreversible, and takes
 *  an integrator's production traffic offline — a misclick on a busy screen is a real
 *  risk, and a confirm button is not enough friction for that outcome.
 * ════════════════════════════════════════════════════════════════════════════
 */

export function CreateKeyDialog({
  integratorId,
  integratorName,
  open,
  onOpenChange,
}: {
  integratorId: string;
  integratorName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [label, setLabel] = useState('');
  const [environment, setEnvironment] = useState<'LIVE' | 'TEST'>('LIVE');
  const [ipAllowlist, setIpAllowlist] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [narrowScopes, setNarrowScopes] = useState(false);
  const [scopes, setScopes] = useState<string[]>([]);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);

  const reset = () => {
    setLabel('');
    setEnvironment('LIVE');
    setIpAllowlist('');
    setExpiresAt('');
    setNarrowScopes(false);
    setScopes([]);
    setCreated(null);
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<CreatedApiKey>(`/api/console/platform/integrators/${integratorId}/keys`, {
        label: label.trim(),
        environment,
        scopes: narrowScopes ? scopes : [],
        ipAllowlist: ipAllowlist
          .split(/[\s,]+/)
          .map((entry) => entry.trim())
          .filter(Boolean),
        expiresAt: expiresAt || null,
      }),
    onSuccess: (key) => {
      setCreated(key);
      void queryClient.invalidateQueries({ queryKey: ['integrator', integratorId] });
    },
    onError: (error) =>
      toast.error('Could not issue the key', error instanceof ApiError ? error.message : ''),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing after creation loses the secret for good, so the close is only allowed
        // from the explicit acknowledgement below while a secret is on screen.
        if (!next && created) return;
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        {created ? (
          <>
            <DialogHeader
              eyebrow={integratorName}
              title="Copy this key now"
              description="This is the only time it will ever be shown. Only a hash is stored, so it cannot be retrieved later — if it is lost, issue a new one."
            />
            <DialogBody className="flex flex-col gap-4">
              <Callout tone="warn" icon={<ShieldAlert className="size-4" />} title="Treat this like a password">
                Send it to {integratorName} over a channel you would use for a password. Anyone
                holding it can lodge certificate requests as this integrator.
              </Callout>

              <Field label="API key" help="Includes the prefix, environment, key id and secret.">
                <SecretReveal value={created.secret} />
              </Field>

              <dl className="m-0 grid gap-x-6 gap-y-2 [font-size:13px] sm:grid-cols-2">
                <div>
                  <dt className="text-[var(--pv-faint)]">Key id (safe to log)</dt>
                  <dd className="pv-mono m-0 font-medium">{created.keyId}</dd>
                </div>
                <div>
                  <dt className="text-[var(--pv-faint)]">Environment</dt>
                  <dd className="m-0 font-medium">{created.environment}</dd>
                </div>
              </dl>
            </DialogBody>
            <DialogFooter note="The key id is what appears in audit rows and usage reports. The secret never does.">
              <Button
                variant="primary"
                onClick={() => {
                  reset();
                  onOpenChange(false);
                  toast.success('Key issued', 'It is live immediately.');
                }}
              >
                I have copied it
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader
              eyebrow={integratorName}
              title="Issue an API key"
              description="A key can only ever be narrower than the integrator's chamber grants — never broader."
            />
            <DialogBody className="flex flex-col gap-4">
              <Field
                label="Label"
                required
                help="How you will recognise this key later. It is also what you must type to revoke it."
              >
                <Input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="SageDocs — production"
                  autoFocus
                />
              </Field>

              <fieldset className="m-0 border-0 p-0">
                <legend className="mb-2 [font-size:var(--pv-text-xs)] font-semibold text-[var(--pv-ink-soft)]">
                  Environment
                </legend>
                <RadioGroup
                  value={environment}
                  onValueChange={(v) => setEnvironment(v as 'LIVE' | 'TEST')}
                  className="grid gap-2 sm:grid-cols-2"
                >
                  <RadioCard
                    value="LIVE"
                    title="Live"
                    description="Issues real certificates against real chambers."
                  />
                  <RadioCard
                    value="TEST"
                    title="Test"
                    description="Sandbox chambers only. Certificates are watermarked SPECIMEN and draw from a separate sequence."
                  />
                </RadioGroup>
              </fieldset>

              <Checkbox
                checked={narrowScopes}
                onCheckedChange={(v) => {
                  setNarrowScopes(v === true);
                  if (v !== true) setScopes([]);
                }}
                label="Restrict what this key can do"
                description="By default it inherits everything the integrator's grants allow. Restrict it when you are handing a key to a contractor or a staging deployment."
              />

              {narrowScopes && (
                <div className="flex flex-col gap-2 rounded-[var(--pv-radius)] border border-[var(--pv-line)] bg-[var(--pv-card-alt)] p-3.5">
                  {ALL_API_SCOPES.map((scope) => (
                    <Checkbox
                      key={scope}
                      checked={scopes.includes(scope)}
                      onCheckedChange={(checked) =>
                        setScopes((prev) =>
                          checked === true ? [...prev, scope] : prev.filter((s) => s !== scope),
                        )
                      }
                      label={<span className="pv-mono [font-size:12.5px]">{scope}</span>}
                    />
                  ))}
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="IP allowlist"
                  help="Addresses or CIDR ranges, one per line. Leave blank for no restriction — an integrator on autoscaled infrastructure often cannot name its egress addresses."
                >
                  <Textarea
                    value={ipAllowlist}
                    onChange={(e) => setIpAllowlist(e.target.value)}
                    rows={3}
                    placeholder="203.0.113.0/24"
                    className="pv-mono [font-size:12.5px]"
                  />
                </Field>
                <Field label="Expires" help="Optional. A key with no expiry works until it is revoked or rotated.">
                  <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
                </Field>
              </div>
            </DialogBody>
            <DialogFooter note="The secret will be shown once, on the next screen.">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                disabled={!label.trim() || (narrowScopes && scopes.length === 0)}
                loading={create.isPending}
                onClick={() => create.mutate()}
                leadingIcon={<KeyRound className="size-3.5" />}
              >
                Issue the key
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RotateKeyDialog({
  keyRowId,
  keyLabel,
  integratorId,
  open,
  onOpenChange,
}: {
  keyRowId: string;
  keyLabel: string;
  integratorId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [overlapDays, setOverlapDays] = useState(30);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);

  const rotate = useMutation({
    mutationFn: () =>
      api.post<CreatedApiKey>(`/api/console/platform/integrators/keys/${keyRowId}/rotate`, { overlapDays }),
    onSuccess: (key) => {
      setCreated(key);
      void queryClient.invalidateQueries({ queryKey: ['integrator', integratorId] });
    },
    onError: (error) =>
      toast.error('Could not rotate the key', error instanceof ApiError ? error.message : ''),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && created) return;
        if (!next) setCreated(null);
        onOpenChange(next);
      }}
    >
      <DialogContent>
        {created ? (
          <>
            <DialogHeader
              title="Copy the replacement key now"
              description={`The old key keeps working for ${overlapDays} days, so the integrator can deploy this on their own schedule.`}
            />
            <DialogBody className="flex flex-col gap-4">
              <Field label="New API key">
                <SecretReveal value={created.secret} />
              </Field>
              <Callout tone="info" icon={<Check className="size-4" />} title="Nothing breaks right now">
                Both keys are valid until the overlap expires. Once the integrator confirms the
                new key is live, you can revoke the old one early.
              </Callout>
            </DialogBody>
            <DialogFooter>
              <Button
                variant="primary"
                onClick={() => {
                  setCreated(null);
                  onOpenChange(false);
                  toast.success('Key rotated');
                }}
              >
                I have copied it
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader
              eyebrow={keyLabel}
              title="Rotate this key"
              description="Issues a replacement and schedules this key to stop working. Both work during the overlap."
            />
            <DialogBody className="flex flex-col gap-4">
              <Field
                label="Overlap"
                help="How long the current key keeps working. Long enough for the integrator to deploy without a rush."
              >
                <NativeSelect
                  value={overlapDays}
                  onChange={(e) => setOverlapDays(Number(e.target.value))}
                >
                  <option value={0}>No overlap — the old key stops immediately</option>
                  <option value={7}>7 days</option>
                  <option value={14}>14 days</option>
                  <option value={30}>30 days (recommended)</option>
                  <option value={60}>60 days</option>
                  <option value={90}>90 days</option>
                </NativeSelect>
              </Field>
              {overlapDays === 0 && (
                <Callout tone="warn" icon={<AlertTriangle className="size-4" />}>
                  With no overlap, the integrator's production traffic fails from the moment you
                  rotate until they deploy the new key. Only choose this if the current key is
                  compromised — and in that case, revoke it instead.
                </Callout>
              )}
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button variant="primary" loading={rotate.isPending} onClick={() => rotate.mutate()}>
                Rotate
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function RevokeKeyDialog({
  keyRowId,
  keyLabel,
  integratorId,
  open,
  onOpenChange,
}: {
  keyRowId: string;
  keyLabel: string;
  integratorId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirmLabel, setConfirmLabel] = useState('');
  const [reason, setReason] = useState('');

  const confirmed = confirmLabel.trim() === keyLabel.trim();

  const revoke = useMutation({
    mutationFn: () =>
      api.post(`/api/console/platform/integrators/keys/${keyRowId}/revoke`, {
        confirmLabel: confirmLabel.trim(),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['integrator', integratorId] });
      onOpenChange(false);
      setConfirmLabel('');
      setReason('');
      toast.success('Key revoked', 'It stopped working immediately.');
    },
    onError: (error) =>
      toast.error('Could not revoke the key', error instanceof ApiError ? error.message : ''),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader
          eyebrow={keyLabel}
          title="Revoke this key"
          description="Immediate and irreversible. Any request using it starts failing within a second."
        />
        <DialogBody className="flex flex-col gap-4">
          <Callout tone="stop" icon={<AlertTriangle className="size-4" />}>
            If the integrator is using this key in production, their traffic stops now. To replace
            it without an outage, <strong>rotate</strong> instead.
          </Callout>

          <Field label="Reason" required help="Recorded in the audit trail with your name.">
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Key was exposed in a support ticket."
            />
          </Field>

          <Field
            label={`Type the key label to confirm`}
            required
            error={confirmLabel && !confirmed ? 'That does not match the label.' : null}
            help={keyLabel}
          >
            <Input
              value={confirmLabel}
              onChange={(e) => setConfirmLabel(e.target.value)}
              autoComplete="off"
            />
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!confirmed || !reason.trim()}
            loading={revoke.isPending}
            onClick={() => revoke.mutate()}
          >
            Revoke now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * A secret, masked until revealed and copyable in one action.
 *
 * Masked by default because this dialog is often open while someone is screen-sharing
 * with the integrator they are issuing the key to.
 */
export function SecretReveal({ value }: { value: string }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permission denied, or a non-secure context. Revealing the value lets
      // the person select it by hand, which is the only remaining option.
      setRevealed(true);
    }
  };

  return (
    <div className="flex items-stretch gap-2">
      <code className="min-w-0 flex-1 break-all rounded-[var(--pv-radius)] border border-[var(--pv-line-strong)] bg-[var(--pv-card-alt)] px-3 py-2.5 [font-size:12.5px] leading-relaxed">
        {revealed ? value : `${value.slice(0, 16)}${'•'.repeat(28)}`}
      </code>
      <div className="flex shrink-0 flex-col gap-1">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setRevealed((v) => !v)}
          leadingIcon={<Eye className="size-3.5" />}
        >
          {revealed ? 'Hide' : 'Reveal'}
        </Button>
        <Button
          variant={copied ? 'accent' : 'primary'}
          size="sm"
          onClick={() => void copy()}
          leadingIcon={copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}
